import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

const ProjectExpoSubmission = () => {
  const [formData, setFormData] = useState({
    // Project Details
    projectTitle: '',
    projectDescription: '',
    category: '',
    
    // Links
    githubUrl: '',
    demoLink: '',
    liveAppLink: '',
    
    // Team Details
    teamName: '',
    teamLeadName: '',
    teamLeadEmail: '',
    institution: '',
    city: '',
    state: '',
    
    // Declarations
    projectOwnership: false,
    githubConsent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const docRef = await addDoc(collection(db, 'projectExpoSubmissions'), {
        ...formData,
        submittedAt: serverTimestamp(),
        eventName: 'AI Day @ Anurag University',
        eventDate: '2026-01-05'
      });
      
      console.log('Project submission saved with ID:', docRef.id);
      toast.success('Project submitted successfully! We will contact you soon.');
      
      // Reset form
      setFormData({
        projectTitle: '', projectDescription: '', category: '',
        githubUrl: '', demoLink: '', liveAppLink: '',
        teamName: '', teamLeadName: '', teamLeadEmail: '',
        institution: '', city: '', state: '',
        projectOwnership: false, githubConsent: false
      });
    } catch (error) {
      console.error('Error submitting project:', error);
      toast.error('Error submitting project. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/events/anurag-university" className="inline-flex items-center gap-2 text-crimson hover:text-black transition-colors mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Event Page
          </Link>
          <h1 className="text-4xl md:text-5xl font-outfit font-800 text-black mb-4">
            Project Expo Submission
          </h1>
          <p className="text-lg font-outfit font-600 text-gray-600">
            AI Day @ Anurag University · 5 January 2026
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Project Details */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">01</span> Project Details
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Project Title <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="projectTitle"
                  value={formData.projectTitle}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="Enter your project title"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Short Description (2-3 lines) <span className="text-crimson">*</span>
                </label>
                <textarea
                  name="projectDescription"
                  value={formData.projectDescription}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit resize-none"
                  placeholder="What the project does / what problem it solves..."
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Category / Track <span className="text-crimson">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit bg-white"
                >
                  <option value="">Select category</option>
                  <option value="ai-ml">AI / ML</option>
                  <option value="computer-vision">Computer Vision</option>
                  <option value="nlp-llms">NLP / LLMs</option>
                  <option value="generative-ai">Generative AI / Agents</option>
                  <option value="data-science">Data Science / Analytics</option>
                  <option value="robotics-iot">Robotics / IoT</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Links */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">02</span> Links
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  GitHub Repository URL <span className="text-crimson">*</span>
                </label>
                <input
                  type="url"
                  name="githubUrl"
                  value={formData.githubUrl}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="https://github.com/username/project"
                />
                <p className="text-sm text-gray-500 mt-1 font-outfit">
                  Must be public or reviewer-accessible
                </p>
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Demo / Video Link (Optional)
                </label>
                <input
                  type="url"
                  name="demoLink"
                  value={formData.demoLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="YouTube / Drive / Loom link"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Live App / Deployed Link (Optional)
                </label>
                <input
                  type="url"
                  name="liveAppLink"
                  value={formData.liveAppLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="https://your-app.com"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Team Details */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">03</span> Team Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Name <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="Enter your team name"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Lead Name <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="teamLeadName"
                  value={formData.teamLeadName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="Full name"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Lead Email <span className="text-crimson">*</span>
                </label>
                <input
                  type="email"
                  name="teamLeadEmail"
                  value={formData.teamLeadEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="email@example.com"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Institution / Organization <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="University or Company name"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  City (Optional)
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="City"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  State (Optional)
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="State"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Declaration */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">04</span> Declaration
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="projectOwnership"
                  checked={formData.projectOwnership}
                  onChange={handleChange}
                  required
                  className="mt-1 w-5 h-5 border-2 border-gray-300 rounded focus:ring-crimson text-crimson"
                />
                <label className="text-sm font-outfit font-600 text-gray-700">
                  I confirm this project belongs to our team. <span className="text-crimson">*</span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="githubConsent"
                  checked={formData.githubConsent}
                  onChange={handleChange}
                  required
                  className="mt-1 w-5 h-5 border-2 border-gray-300 rounded focus:ring-crimson text-crimson"
                />
                <label className="text-sm font-outfit font-600 text-gray-700">
                  I agree to submit the original GitHub repository. <span className="text-crimson">*</span>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              className="btn-primary text-lg px-12 py-4"
            >
              Submit Project
            </button>
            <p className="text-sm text-gray-500 mt-4 font-outfit">
              All fields marked with <span className="text-crimson">*</span> are required
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectExpoSubmission;
